import React, { useEffect, useMemo, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./BigCommerceInvoiceLists.module.css";
import { getAllBigCommerceInvoices } from "../../../api/api";
import { SearchContext } from "../../../context/SearchContext";
import Pagination from "../../../components/layout/Pagination/Pagination";

const ITEMS_PER_PAGE = 10;

const BigCommerceInvoiceLists = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();

  const { searchTerm } = useContext(SearchContext);

  useEffect(() => {
    const fetchInvoices = async () => {
      try {
        const res = await getAllBigCommerceInvoices();
        setInvoices(res.data || []);
      } catch (error) {
        console.error("Failed to fetch invoices", error);
      } finally {
        setLoading(false);
      }
    };
    fetchInvoices();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const filteredInvoices = useMemo(() => {
    const q = (searchTerm || "").trim().toLowerCase();
    if (!q) return invoices;

    return invoices.filter((inv) => {
      const name = (inv.customer_name || "").toLowerCase();
      const orderId = String(inv.order_id ?? "").toLowerCase();
      const invoiceNum = String(inv.invoice_number ?? "").toLowerCase();
      return (
        name.includes(q) ||
        orderId.includes(q) ||
        invoiceNum.includes(q)
      );
    });
  }, [invoices, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filteredInvoices.length / ITEMS_PER_PAGE));

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1);
  }, [currentPage, totalPages]);

  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const displayedInvoices = useMemo(
    () => filteredInvoices.slice(startIndex, startIndex + ITEMS_PER_PAGE),
    [filteredInvoices, startIndex]
  );

  return (
    <div className={styles.invoiceContainer}>
      <h2>BigCommerce Invoices</h2>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <>
          <table className={styles.invoiceTable}>
            <thead>
              <tr>
                <th>#</th>
                <th>Customer Name</th>
                <th>Order ID</th>
                <th>Date</th>
                <th>Edit Invoice</th>
              </tr>
            </thead>
            <tbody>
              {displayedInvoices.length > 0 ? (
                displayedInvoices.map((inv, index) => (
                  <tr key={inv.id ?? `${inv.invoice_number}-${index}`}>
                    <td>{startIndex + index + 1}</td>
                    <td>{inv.customer_name || "Unknown"}</td>
                    <td>{inv.order_id ?? "-"}</td>
                    <td>
                      {inv.created_at
                        ? new Date(inv.created_at).toLocaleDateString()
                        : "-"}
                    </td>
                    <td>
                      <button
                        className={styles.viewButton}
                        onClick={() =>
                          navigate(`/superadmin/bigcommerce-invoices/${inv.id}`)
                        }
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" style={{ textAlign: "center" }}>
                    No invoices found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {filteredInvoices.length > ITEMS_PER_PAGE && (
            <Pagination
              currentPage={currentPage}
              totalPages={Math.ceil(filteredInvoices.length / ITEMS_PER_PAGE)}
              onPageChange={setCurrentPage}
            />
          )}
        </>
      )}
    </div>
  );
};

export default BigCommerceInvoiceLists;
